import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, PhoneCall } from "lucide-react";
import { Button } from "./ui/button";
import Logo from "./Logo";
import { navLinks, contactInfo } from "../mock";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location]);

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/#")) {
      const id = href.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 80);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(href);
      window.scrollTo({ top: 0 });
    }
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "border-b border-white/10 bg-[#04060a]/85 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 lg:px-8">
        <Link to="/" onClick={() => window.scrollTo({ top: 0 })} aria-label="Arkision AI home">
          <Logo />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex">
          {navLinks.map((l) => (
            <button
              key={l.label}
              onClick={() => go(l.href)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                (l.href === "/industries/healthcare" && location.pathname.startsWith("/industries"))
                  ? "text-cyan-300"
                  : "text-slate-300 hover:bg-white/5 hover:text-white"
              }`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href="tel:+918960969291"
            className="flex items-center gap-2 text-sm font-medium text-slate-300 transition-colors hover:text-cyan-300"
          >
            <PhoneCall className="h-4 w-4" /> +91 8960969291
          </a>
          <Button
            onClick={() => go("/#contact")}
            className="rounded-full bg-cyan-400 px-5 text-sm font-semibold text-slate-950 transition-colors duration-200 hover:bg-cyan-300"
          >
            Book a Demo
          </Button>
        </div>

        {/* Mobile toggle */}
        <button
          className="rounded-lg border border-white/10 p-2 text-white lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="border-t border-white/10 bg-[#04060a]/95 px-5 pb-6 pt-3 backdrop-blur-xl lg:hidden">
          {navLinks.map((l) => (
            <button
              key={l.label}
              onClick={() => go(l.href)}
              className="block w-full rounded-lg px-3 py-3 text-left text-base font-medium text-slate-200 transition-colors hover:bg-white/5 hover:text-white"
            >
              {l.label}
            </button>
          ))}
          <Button
            onClick={() => go("/#contact")}
            className="mt-3 w-full rounded-full bg-cyan-400 font-semibold text-slate-950 hover:bg-cyan-300"
          >
            Book a Demo
          </Button>
        </div>
      )}
    </header>
  );
};

export default Navbar;
